import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useDebouncedValue } from '../use_debounced_value.js';
import type { PersonItem } from './person_row.js';
import type { TravelerSearchState } from './traveler_picker.js';

/** Um viajante como o pedido o guarda: `{id, name}`. */
type TravelerRef = { readonly id: string; readonly name: string };

/** Um resultado da busca de viajantes: o viajante e o que só a linha mostra. */
type TravelerSearchResult = TravelerRef & { subtitle?: string; photoUrl?: string | null };

/** A busca paginada do roster da empresa que o host injeta (o
 *  `TravelerDirectoryPort` do genui-kit). `term` ausente ⇒ 1ª página de todos. */
export type TravelerDirectory = {
  searchTravelers: (params: {
    term?: string;
    page?: number;
  }) => Promise<{ items: TravelerSearchResult[]; hasNext: boolean }>;
  viewerPhotoUrl?: string | null;
};

/*
 * A busca de viajantes do QUEM, compartilhada pelo QUEM do voo
 * (`TravelersField`) e pelo do ônibus na conversa (`bus_planner` do genui-kit):
 * o termo com debounce, a página, o estado e as fotos já vistas, por id. Busca
 * só enquanto `open`.
 */

export function toPerson(traveler: TravelerSearchResult): PersonItem {
  return {
    id: traveler.id,
    name: traveler.name,
    ...(traveler.subtitle ? { description: traveler.subtitle } : {}),
    ...(traveler.photoUrl ? { photoUrl: traveler.photoUrl } : {}),
  };
}

/** As fotos de quem já passou pela busca, por id. O `travelers[]` do bloco é
 *  `{id,name}` cru, então o gatilho, a linha "Você" e o grupo Na viagem só têm
 *  foto quando a pessoa apareceu em alguma página da busca — e a mantêm mesmo
 *  depois que a busca filtra ela para fora. */
export function withPhotos(
  travelers: readonly TravelerRef[],
  photos: ReadonlyMap<string, string>,
): PersonItem[] {
  return travelers.map((traveler) => {
    const photoUrl = photos.get(traveler.id);
    return photoUrl ? { ...traveler, photoUrl } : traveler;
  });
}

/** `photos` with the photos these results carry; the same map when none is new. */
function rememberPhotos(
  photos: ReadonlyMap<string, string>,
  items: readonly TravelerSearchResult[],
): ReadonlyMap<string, string> {
  const fresh = items.filter((item) => item.photoUrl && photos.get(item.id) !== item.photoUrl);
  if (fresh.length === 0) return photos;
  const next = new Map(photos);
  fresh.forEach((item) => {
    if (item.photoUrl) next.set(item.id, item.photoUrl);
  });
  return next;
}

/** `photos` under the host's word on the viewer's photo: a URL wins over what the
 *  search brought, `null` takes it out. The same map when nothing changes. */
function withViewerPhoto(
  photos: ReadonlyMap<string, string>,
  viewer: { readonly id: string; readonly photoUrl: string | null } | undefined,
): ReadonlyMap<string, string> {
  if (!viewer) return photos;
  const { id, photoUrl } = viewer;
  if (photoUrl ? photos.get(id) === photoUrl : !photos.has(id)) return photos;
  const next = new Map(photos);
  if (photoUrl) next.set(id, photoUrl);
  else next.delete(id);
  return next;
}

export function useTravelerSearch(
  open: boolean,
  searchTravelers: TravelerDirectory['searchTravelers'] | undefined,
  /** A foto do solicitante vinda do host (`viewerPhotoUrl`), semeada no mapa
   *  antes de qualquer busca — a linha "Você" já nasce com ela. `null` é o host
   *  dizendo "sem foto" (ex.: acabou de removê-la): a entrada sai do mapa. */
  viewer?: { readonly id: string; readonly photoUrl: string | null },
) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<TravelerSearchResult[]>([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [state, setState] = useState<TravelerSearchState>('ready');
  /** The query the first page on screen answers; `null` while closed. */
  const [answered, setAnswered] = useState<string | null>(null);
  const requestSequence = useRef(0);
  const query = useDebouncedValue(term, 300).trim();
  const [seenPhotos, setPhotos] = useState(() => withViewerPhoto(new Map(), viewer));
  // The host's word on the viewer is applied on every render, over what the search
  // brought — and it stays after the host stops saying it.
  const photos = withViewerPhoto(seenPhotos, viewer);
  if (photos !== seenPhotos) setPhotos(photos);
  const remember = (items: readonly TravelerSearchResult[]) => {
    setPhotos((current) => rememberPhotos(current, items));
  };
  // A busca re-dispara quando o diretório aparece ou some, não a cada identidade nova dele.
  const searchable = Boolean(searchTravelers);
  const active = open && searchable;
  // Closed (or without a directory): the list goes, and the next opening loads anew.
  if (!active && answered !== null) {
    setAnswered(null);
    setResults([]);
    setHasNext(false);
    setState('ready');
  }
  const searchNow = useEffectEvent((params: { term?: string; page?: number }) =>
    searchTravelers?.(params),
  );

  useEffect(() => {
    if (!open || !searchable) return;
    const request = searchNow({ ...(query ? { term: query } : {}), page: 0 });
    if (!request) return;
    const sequence = ++requestSequence.current;
    request
      .then((response) => {
        if (sequence !== requestSequence.current) return;
        setPhotos((current) => rememberPhotos(current, response.items));
        setResults(response.items);
        setHasNext(response.hasNext);
        setPage(0);
        setAnswered(query);
        setState('ready');
      })
      .catch(() => {
        if (sequence !== requestSequence.current) return;
        setResults([]);
        setHasNext(false);
        setAnswered(query);
        setState('error');
      });
  }, [open, searchable, query]);

  const loadMore = () => {
    if (!searchTravelers) return;
    const nextPage = page + 1;
    const sequence = ++requestSequence.current;
    setState('loading');
    searchTravelers({ ...(query ? { term: query } : {}), page: nextPage })
      .then((response) => {
        if (sequence !== requestSequence.current) return;
        remember(response.items);
        setResults((previous) => [...previous, ...response.items]);
        setHasNext(response.hasNext);
        setPage(nextPage);
        setState('ready');
      })
      .catch(() => {
        if (sequence !== requestSequence.current) return;
        setState('error');
      });
  };

  // The first page of a new query (or of a new opening) is in flight.
  const shownState = active && answered !== query ? 'loading' : state;
  return { term, setTerm, results, hasNext, state: shownState, loadMore, photos };
}
